import {
  ELEMENT_TYPES,
  PERSIST_ROWS,
  ALIGN_TO_JUSTIFY,
} from './constants';

/* ---------------------------------------------------------------------- */
/* فیلترها و منابع داده                                                    */
/* ---------------------------------------------------------------------- */

// تعداد منابع/ستون‌های هر فیلتر را از details می‌خواند (برای نمایش در لیست تب فیلتر)
export function getFilterSourcesCount(f) {
  try {
    const d = typeof f?.details === 'string' ? JSON.parse(f.details) : f?.details;
    if (Array.isArray(d?.sources)) return d.sources.length;
  } catch (e) { /* ignore */ }
  return f?.sDataSourceId ? 1 : 0;
}

// details یک فیلتر (رشته‌ی JSON یا آبجکت) را به آبجکت تبدیل می‌کند
export function parseFilterDetails(f) {
  const d = f?.details;
  if (d === undefined || d === null || d === '') return undefined;
  if (typeof d === 'object') return d;
  try {
    return JSON.parse(d);
  } catch (e) {
    return undefined;
  }
}

export function parseDataSourceFields(ds) {
  if (!ds?.schema) return [];
  try {
    const cols = typeof ds.schema === 'string' ? JSON.parse(ds.schema) : ds.schema;
    if (!Array.isArray(cols)) return [];
    return cols.map((c) => ({ field: c.name, label: c.name }));
  } catch (e) {
    return [];
  }
}

export function findDataSource(list, id) {
  return (list || []).find((d) => d.id === id) || null;
}

export function extractRows(response) {
  let data = response?.data ?? response;
  if (typeof data === 'string') {
    try { data = JSON.parse(data); } catch (e) { return []; }
  }
  if (!Array.isArray(data)) data = data?.rows ?? data?.items ?? data?.result ?? data?.data ?? [];
  if (typeof data === 'string') {
    try { data = JSON.parse(data); } catch (e) { return []; }
  }
  return Array.isArray(data) ? data : [];
}

export function normalizeRowKeys(rows, fields) {
  const map = {};
  (fields || []).forEach((f) => { map[String(f.field).toLowerCase()] = f.field; });
  return rows.map((row) => {
    if (!row || typeof row !== 'object') return {};
    const out = {};
    Object.keys(row).forEach((k) => {
      out[map[k.toLowerCase()] ?? k] = row[k];
    });
    return out;
  });
}

export function buildServerConditions(filters = [], externalParams = {}, externalValues = {}) {
  return (filters || []).flatMap((f) => {
    if (!f.field) return [];
    const v = f.source === 'external'
      ? (externalParams?.[f.paramKey] ?? externalValues?.[f.paramKey])
      : f.value;
    if (v === undefined || v === null || String(v).trim() === '') return [];
    return [{ columnName: f.field, operator: f.operator || 'eq', values: [String(v).trim()] }];
  });
}

export const toLatinDigits = (s) =>
  String(s)
    .replace(/[۰-۹]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d))
    .replace(/[٠-٩]/g, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d));

// فیلترهای سایدبار پیش‌نمایش برای یک منبع داده
export function buildFilterConditions(activeFilters = [], dataSourceId) {
  return (activeFilters || []).flatMap((f) => {
    let vals = (f.value || []).filter((x) => x !== '' && x != null).map(String);
    if (f.filterType === 5) vals = vals.map(toLatinDigits);
    if (!vals.length) return [];

    let operator = 'eq';
    let values = [vals[0]];
    if (f.isRange && vals.length >= 2) { operator = 'between'; values = [vals[0], vals[1]]; }
    else if (f.filterType === 2 || f.filterType === 4) { operator = 'in'; values = vals; }

    return (f.sources || [])
      .filter((s) => s.dataSourceId === dataSourceId)
      .map((s) => ({ columnName: s.colName, operator, values }));
  });
}

export async function fetchDataRows(fetcher, { tableName, fields, filters = [], externalParams = {}, externalValues = {}, extraConditions = [] }) {
  const conditions = [...buildServerConditions(filters, externalParams, externalValues), ...extraConditions];
  const cols = Array.from(new Set([
    ...fields.map((f) => f.field),
    ...(filters || []).map((f) => f.field).filter(Boolean),
    ...extraConditions.map((c) => c.columnName),
  ]));
  const response = await fetcher({
    tabelName: tableName,
    Columns: cols,
    conditions,
  });
  return normalizeRowKeys(extractRows(response), cols.map((field) => ({ field })));
}

export function stripRows(sections) {
  if (PERSIST_ROWS) return sections;
  const out = {};
  ['header', 'detail', 'footer'].forEach((k) => {
    out[k] = {
      ...sections[k],
      elements: sections[k].elements.map((el) =>
        el.type === ELEMENT_TYPES.DATA ? { ...el, props: { ...el.props, rows: [] } } : el
      ),
    };
  });
  return out;
}

/* ---------------------------------------------------------------------- */
/* اعمال شرط‌ها روی ردیف‌ها (سمت کلاینت)                                    */
/* ---------------------------------------------------------------------- */

export const toNumber = (v) => Number(String(v ?? '').replace(/,/g, '').trim());

export function compareValues(op, cell, target) {
  const a = String(cell ?? '').trim().toLowerCase();
  const b = String(target ?? '').trim().toLowerCase();
  switch (op) {
    case 'eq': return a === b;
    case 'neq': return a !== b;
    case 'contains': return a.includes(b);
    case 'gt': return toNumber(a) > toNumber(b);
    case 'lt': return toNumber(a) < toNumber(b);
    default: return true;
  }
}

export function resolveFilterValue(f, externalValues, externalParams) {
  if (f.source === 'external') return externalParams?.[f.paramKey] ?? externalValues?.[f.paramKey];
  return f.value;
}

export function applyFilters(rows, filters = [], externalValues = {}, externalParams = {}) {
  const active = filters.filter((f) => {
    if (!f.field) return false;
    if (f.source === 'external') {
      const v = resolveFilterValue(f, externalValues, externalParams);
      return v !== undefined && v !== null && v !== '';
    }
    return true;
  });
  if (!active.length) return rows;
  return rows.filter((row) =>
    active.every((f) => compareValues(f.operator, row[f.field], resolveFilterValue(f, externalValues, externalParams)))
  );
}

export function getElementRows(el, externalParams) {
  const rows = el.props.rows || [];
  if (el.type !== ELEMENT_TYPES.DATA) return rows;
  return applyFilters(rows, el.props.filters, el.props.externalValues, externalParams);
}

export function getReportExternalVariables(sections) {
  const out = [];
  ['header', 'detail', 'footer'].forEach((k) => {
    (sections?.[k]?.elements || []).forEach((el) => {
      if (el.type !== ELEMENT_TYPES.DATA) return;
      (el.props.filters || []).forEach((f) => {
        if (f.source === 'external' && f.paramKey) {
          out.push({ key: f.paramKey, dataElementId: el.id, dataSourceId: el.props.dataSourceId, field: f.field, operator: f.operator });
        }
      });
    });
  });
  return out;
}

export function normalizeReport(report) {
  if (!report) return null;
  let s = report.Items ?? report.items ?? report.sectionss ?? report;
  if (typeof s === 'string') {
    try { s = JSON.parse(s); } catch (e) { return null; }
  }
  if (!s?.header || !s?.detail || !s?.footer) return null;
  return s;
}

export function injectDataSources(sections, dataSources) {
  if (!dataSources) return sections;
  const out = {};
  ['header', 'detail', 'footer'].forEach((k) => {
    out[k] = {
      ...sections[k],
      elements: sections[k].elements.map((el) => {
        if (el.type !== ELEMENT_TYPES.DATA) return el;
        const rows = dataSources[el.id] ?? dataSources[el.props.dataSourceId];
        return Array.isArray(rows) ? { ...el, props: { ...el.props, rows } } : el;
      }),
    };
  });
  return out;
}

/* ---------------------------------------------------------------------- */
/* شناسه و محاسبات چیدمان                                                  */
/* ---------------------------------------------------------------------- */

let idCounter = 1;
export function generateId(prefix = 'el') {
  idCounter += 1;
  return `${prefix}_${Date.now()}_${idCounter}`;
}

export function computeRowsFit(containerHeight, rowHeight) {
  const bodyHeight = containerHeight - rowHeight;
  return Math.max(1, Math.floor(bodyHeight / rowHeight));
}

/*
  تعداد «پنل دیتا» که در حالت پشت‌سرهم در یک صفحه جا می‌شوند.

  هر پنل دیتا (کادر آیتم دیتا با همه‌ی آیتم‌های داخلش) یک رکورد است.
  پنل i‌ام به اندازه‌ی i * ارتفاع کادر پایین‌تر از پنل قبلی چیده می‌شود، پس
  تعداد پنل‌ها = فضای باقی‌مانده‌ی بخش از بالای کادر تا انتهای بخش / ارتفاع کادر.
*/
export function computeStackedRecordsPerPage(sections, dataSectionKey, dataEl) {
  const sec = sections?.[dataSectionKey];
  if (!sec) return 1;
  const frameH = Math.max(1, dataEl.height);
  return Math.max(1, Math.floor((sec.height - dataEl.y + 0.001) / frameH));
}

/* ---------------------------------------------------------------------- */
/* استایل آیتم‌ها (فونت/رنگ/...) — همه از پنل FontPicker                   */
/* در props.style ذخیره می‌شود؛ گزارش‌های قدیمی با props.fontSize / color / */
/* align / fill هم هنوز درست رندر می‌شوند (fallback).                       */
/* ---------------------------------------------------------------------- */

// هندلرهای FontPicker ممکن است مقدار مستقیم یا event بدهند؛ هر دو پشتیبانی می‌شود
export function pickValue(v) { return v && typeof v === 'object' && v.target !== undefined ? v.target.value : v; }
export function pickChecked(v) { return v && typeof v === 'object' && v.target !== undefined ? v.target.checked : v; }
export function pickColor(c) {
  if (c == null || typeof c === 'string') return c;
  if (typeof c.toHexString === 'function') return c.toHexString();
  if (c.hex) return c.hex;
  if (c.target !== undefined) return c.target.value;
  return c;
}
export function normalizeWeight(v) { if (v === true) return 'bold'; if (v === false) return 'normal'; return v; }
export function normalizeFontStyle(v) { if (v === true) return 'italic'; if (v === false) return 'normal'; return v; }
export function normalizeSize(v) {
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return v;
}

export function getStyleValues(type, props) {
  const p = props || {};
  const st = p.style || {};
  const legacyColor = type === ELEMENT_TYPES.RECTANGLE ? p.fill : p.color;
  return {
    open: st.open ?? true,
    fontFamily: st.fontFamily,
    fontZaryb: st.fontZaryb,
    fontSize: st.fontSize ?? p.fontSize,
    fontFormat: st.fontFormat,
    color: st.color ?? legacyColor,
    fontWeight: st.fontWeight ?? p.fontWeight,
    fontStyle: st.fontStyle,
    textAlign: st.textAlign ?? p.align,
  };
}

export function resolveTextStyle(type, props) {
  const v = getStyleValues(type, props);
  const css = {};
  if (v.fontFamily) css.fontFamily = v.fontFamily;
  if (v.fontSize !== undefined && v.fontSize !== '') css.fontSize = normalizeSize(v.fontSize);
  if (v.fontWeight !== undefined) css.fontWeight = normalizeWeight(v.fontWeight);
  if (v.fontStyle !== undefined) css.fontStyle = normalizeFontStyle(v.fontStyle);
  if (v.color) css.color = v.color;
  if (v.textAlign) {
    css.textAlign = v.textAlign;
    css.justifyContent = ALIGN_TO_JUSTIFY[v.textAlign] || undefined;
  }
  return { css, zaryb: v.fontZaryb, format: v.fontFormat };
}

/* ---------------------------------------------------------------------- */
/* فرمت مقدارها                                                            */
/* ---------------------------------------------------------------------- */

// ارقام لاتین را به فارسی تبدیل می‌کند (فقط برای نمایش؛ دیتا تغییر نمی‌کند)
export function toFaDigits(str) {
  return String(str).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d]);
}

// ضریب (zaryb) روی مقدارهای عددی ضرب می‌شود و فرمت (format) جداکننده‌ی هزارگان می‌گذارد.
// اگر معنی این دو در FontPicker شما فرق دارد، فقط همین تابع را تغییر بده.
export function formatCellValue(value, ts) {
  const raw = value ?? '';
  if (raw === '' || typeof raw === 'object') return toFaDigits(raw);
  const num = Number(String(raw).replace(/,/g, '').trim());
  if (!Number.isFinite(num)) return toFaDigits(raw);
  const zaryb = Number(ts?.zaryb);
  const hasZaryb = ts?.zaryb !== undefined && ts.zaryb !== '' && Number.isFinite(zaryb) && zaryb !== 1;
  const n = hasZaryb ? num * zaryb : num;
  const f = ts?.format;

  // format به‌صورت آبجکت { decimal, ... } از FontPicker می‌آید
  if (f && typeof f === 'object') {
    const decimal = Math.max(0, Math.min(10, Number(f.decimal) || 0));
    const grouping = !(f.thousand === false || f.separator === false || f.comma === false);
    return n.toLocaleString('fa-IR', { minimumFractionDigits: decimal, maximumFractionDigits: decimal, useGrouping: grouping });
  }
  if (f && !['none', 'normal', 'default', 'false', '0'].includes(String(f))) {
    return n.toLocaleString('fa-IR', { maximumFractionDigits: 6 });
  }
  return toFaDigits(hasZaryb ? String(n) : String(raw));
}
// filterList یک چارت را از روی یک فیلتر آپدیت می‌کند
export function applyFilterToChart(chart, sources, value, isRange) {
  const matched = (sources || []).filter((s) => s.dataSourceId === chart.sDatasourceId);
  if (!matched.length) return chart;               // چارت به این فیلتر ربطی ندارد

  try {
    const details = JSON.parse(chart.details);
    const prop = details?.config?.sanjabProp;
    if (!prop) return chart;

    let filterList = [...(prop.filterList || [])];

    matched.forEach((source) => {
      const idx = filterList.findIndex((x) => x.filterCol === source.colName);

      if (value.length) {
        const item = { filterArray: value, filterCol: source.colName, operatorName: 'in', isRange };
        if (idx !== -1) filterList[idx] = { ...filterList[idx], ...item };
        else filterList.push(item);
      } else if (idx !== -1) {
        filterList = filterList.filter((_, i) => i !== idx);
      }

      // هماهنگی drill مثل داشبورد
      const drill = prop.drillList;
      if (drill?.steps?.length) {
        const di = drill.steps.findIndex((x) => x.name === source.colName);
        if (di !== -1 && !value.includes(drill.steps[di].value)) {
          const steps = drill.steps
            .filter((x) => x.name !== source.colName)
            .map((x, i) => ({ index: i, level: x.level, name: x.name, typeStep: x.typeStep, value: x.value }));
          prop.drillList = steps.length
            ? { level: steps[steps.length - 1].level, steps }
            : { level: '', steps: [] };
        }
      }
    });

    prop.filterList = filterList;
    return { ...chart, details: JSON.stringify(details) };
  } catch (e) {
    console.error('Error updating report chart filter:', e);
    return chart;
  }
}